import Head from "next/head";
import { useI18n } from "../contexts/I18nContext";
import { LANGUAGE_DOMAINS } from "../data/i18n";

const PERSON_NAME = "\u0141ukasz Frydrych";
const PERSON_ASCII_NAME = "Lukasz Frydrych";
const PERSON_PUBLIC_NAME = "Luke Frydrych";
const SITE_NAME = "xsiadron";
const SOCIAL_LINKS = [
    "https://github.com/xsiadron",
    "https://www.youtube.com/@xsiadron",
    "https://www.instagram.com/xsiadron/",
    "https://www.tiktok.com/@xsiadron",
    "https://twitter.com/xsiadron",
    "https://steamcommunity.com/id/xsiadron/",
    "https://useme.com/pl/roles/contractor/xsiadron,329628/",
];

function stripHtml(value = "") {
    return value.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

function buildStructuredData(projects = [], canonicalUrl, title, description, siteUrl, language) {
    const projectItems = projects.map((project, index) => ({
        "@type": "ListItem",
        position: index + 1,
        item: {
            "@type": "CreativeWork",
            name: project.title,
            url: project.link || canonicalUrl,
            image: project.image ? `${siteUrl}${project.image}` : undefined,
            description: stripHtml(project.description),
            datePublished: project.date,
            author: {
                "@id": `${siteUrl}/#person`,
            },
            keywords: Array.isArray(project.tags) ? project.tags.join(", ") : undefined,
        },
    }));

    return {
        "@context": "https://schema.org",
        "@graph": [
            {
                "@type": "Person",
                "@id": `${siteUrl}/#person`,
                name: PERSON_NAME,
                alternateName: [SITE_NAME, PERSON_PUBLIC_NAME, PERSON_ASCII_NAME],
                url: siteUrl,
                email: "mailto:lukasz.frydrych@xsiadron.com",
                jobTitle: "Indie game, app and web developer",
                knowsAbout: [
                    "Game development",
                    "Web development",
                    "App development",
                    "React",
                    "Next.js",
                    "TypeScript",
                    "C++",
                    "C#",
                    "Kotlin",
                    "Java",
                    "Python",
                    "3D modeling",
                    "Digital illustration",
                ],
                sameAs: SOCIAL_LINKS,
            },
            {
                "@type": "WebSite",
                "@id": `${siteUrl}/#website`,
                name: language === "pl" ? "xsiadron.pl" : "xsiadron.com",
                alternateName: [SITE_NAME, PERSON_NAME, PERSON_PUBLIC_NAME, PERSON_ASCII_NAME],
                url: siteUrl,
                description,
                inLanguage: language,
                publisher: {
                    "@id": `${siteUrl}/#person`,
                },
            },
            {
                "@type": "ProfilePage",
                "@id": `${siteUrl}/#homepage`,
                name: title,
                url: canonicalUrl,
                description,
                inLanguage: language,
                isPartOf: {
                    "@id": `${siteUrl}/#website`,
                },
                about: {
                    "@id": `${siteUrl}/#person`,
                },
                mainEntity: {
                    "@id": `${siteUrl}/#person`,
                },
            },
            {
                "@type": "ItemList",
                "@id": `${siteUrl}/#projects`,
                name: "xsiadron projects",
                itemListElement: projectItems,
            },
        ],
    };
}

export default function Seo({
    title,
    description,
    path = "/",
    image = "/images/AvatarTranslucent.png",
    noindex = false,
    projects = [],
}) {
    const { language, t, translateProject } = useI18n();
    const siteUrl = `https://${LANGUAGE_DOMAINS[language]}`;
    const resolvedTitle = title || t("seo.title");
    const resolvedDescription = description || t("seo.description");
    const canonicalUrl = `${siteUrl}${path === "/" ? "/" : path}`;
    const imageUrl = `${siteUrl}${image}`;
    const localizedProjects = projects.map((project) => translateProject(project));
    const structuredData = buildStructuredData(
        localizedProjects,
        canonicalUrl,
        resolvedTitle,
        resolvedDescription,
        siteUrl,
        t("seo.language")
    );
    const robots = noindex
        ? "noindex, nofollow"
        : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1";
    const alternatePath = path === "/" ? "/" : path;

    return (
        <Head>
            <title>{resolvedTitle}</title>
            <meta name="description" content={resolvedDescription} />
            <meta name="keywords" content={t("seo.keywords").join(", ")} />
            <meta name="robots" content={robots} />
            <meta name="author" content={`${PERSON_NAME} (${PERSON_PUBLIC_NAME})`} />
            <meta name="application-name" content={t("seo.siteName")} />
            <meta name="theme-color" content="#202020" />
            <link rel="canonical" href={canonicalUrl} />
            <link rel="alternate" hrefLang="en" href={`https://${LANGUAGE_DOMAINS.en}${alternatePath}`} />
            <link rel="alternate" hrefLang="pl" href={`https://${LANGUAGE_DOMAINS.pl}${alternatePath}`} />
            <link rel="alternate" hrefLang="x-default" href={`https://${LANGUAGE_DOMAINS.en}${alternatePath}`} />
            <link rel="icon" href="/favicon.ico" sizes="any" />
            <link rel="apple-touch-icon" href="/images/AvatarTranslucent.png" />
            <link rel="manifest" href="/manifest.json" />

            <meta property="og:type" content="website" />
            <meta property="og:site_name" content={t("seo.siteName")} />
            <meta property="og:title" content={resolvedTitle} />
            <meta property="og:description" content={resolvedDescription} />
            <meta property="og:url" content={canonicalUrl} />
            <meta property="og:image" content={imageUrl} />
            <meta property="og:image:width" content="512" />
            <meta property="og:image:height" content="512" />
            <meta property="og:locale" content={t("seo.locale")} />

            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={resolvedTitle} />
            <meta name="twitter:description" content={resolvedDescription} />
            <meta name="twitter:image" content={imageUrl} />
            <meta name="twitter:creator" content="@xsiadron" />

            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
            />
        </Head>
    );
}
